import { useCallback, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Colors } from "../../../utils/colors";
import {
  hasSubscription,
  isSubscriptionPaymentFailed,
  mostRecentSubscription,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import { Subscription } from "@markit/common.types";
import SubscriptionBillingItem from "../../Subscription/Items/SubscriptionBillingItem";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import {
  getAllUserSubscriptions,
  overflowRateForSubscription,
} from "../../../utils/subscriptionUtils";
import { testStripe } from "../../../utils/stripeAccountUtils";

const BillingHistoryPanel = () => {
  const { accountData } = useSelector(getAccountState).account;
  const [userSubscriptions, setUserSubscriptions] = useState<Subscription[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  useAsyncOnMount(async () => {
    setLoading(true);
    const allSubscriptions = await getAllUserSubscriptions(accountData);
    setUserSubscriptions(allSubscriptions);
    setLoading(false);
  });

  const trialSubscription = useMemo(
    () => accountData.customer.state === "TRIAL",
    [accountData.customer.state]
  );

  // Creates a mock upcoming subscription object if there is an active subscription.
  // It creates a new subscription object with the start date set to the end date of the most recent subscription and the end date set to one month later.
  // If the subscription payment has failed, it returns undefined.
  const mockUpcomingSub = useMemo(() => {
    if (
      hasSubscription(accountData) &&
      !isSubscriptionPaymentFailed(accountData.customer.state)
    ) {
      const sub = mostRecentSubscription(accountData);
      if (!sub) {
        return undefined;
      }
      const endDate = new Date(sub.end);
      endDate.setMonth(endDate.getMonth() + 1);

      return {
        ...sub,
        start: sub.end,
        end: endDate.toISOString(),
        message: 0,
        price: sub.price.map((p) => ({ ...p, amountPaid: 0 })),
      };
    }
  }, [accountData]);

  const subscriptionInvoices = useMemo(
    () =>
      mockUpcomingSub
        ? [...userSubscriptions, mockUpcomingSub].reverse()
        : [...userSubscriptions].reverse(),
    [userSubscriptions, mockUpcomingSub]
  );

  const amountDue = useCallback(
    (item: Subscription, index: number) => {
      // Use the actual amount paid from the invoice
      if (item.price[0].amountPaid > 0) {
        return item.price[0].amountPaid / 100;
      }
      // Fallback case: Estimated by baseline and overflow credits
      const prevSub =
        index < subscriptionInvoices.length - 1
          ? subscriptionInvoices[index + 1]
          : undefined;
      const overflowCredits = prevSub
        ? Math.max(0, prevSub.message - prevSub.price[0].limit)
        : 0;
      const overflowRate = prevSub
        ? overflowRateForSubscription(
            prevSub,
            accountData.uid,
            testStripe(accountData.uid)
          )
        : 0;
      const totalCents =
        item.price[0].amount + Math.round(overflowRate * overflowCredits);
      return totalCents / 100;
    },
    [accountData.uid, subscriptionInvoices]
  );

  if (loading && !trialSubscription) {
    return (
      <div className="ColumnCentering" style={{ paddingTop: 150 }}>
        <CircularProgress style={{ color: Colors.GRAY1 }} size={28} />
      </div>
    );
  }

  return subscriptionInvoices.length && !trialSubscription ? (
    <div className="ColumnNormal">
      {subscriptionInvoices.map((item, index) => (
        <div key={index}>
          <SubscriptionBillingItem
            subscription={item}
            lastMonthSubscription={
              index < subscriptionInvoices.length - 1
                ? subscriptionInvoices[index + 1]
                : undefined
            }
            showBaseline={true}
            showOverflow={
              index < subscriptionInvoices.length - 1 &&
              subscriptionInvoices[index + 1].message >
                subscriptionInvoices[index + 1].price[0].limit
            }
            amount={amountDue(item, index)}
            showIcon={true}
            isUpcoming={index === 0}
            displayInvoice={true}
          />
          <HorizontalDivider altMargin={14} />
        </div>
      ))}
    </div>
  ) : (
    <EmptyStateButton
      title="No Billing History"
      description="Your subscription and other charges show up here"
      icon={
        <Icon icon="ion:receipt" height={64} style={{ color: Colors.GRAY1 }} />
      }
      iconBox={84}
      containerStyles={{ paddingTop: 150 }}
    />
  );
};

export default BillingHistoryPanel;
